import React, { useState } from "react";
import { useT } from "@/i18n/I18nContext";
import { PageHeader, Card, Input, Select } from "@/components/ui-kit";
import { Calculator as CalcIcon } from "@phosphor-icons/react";

const PRESETS = {
  forex: { pipValue: 10, label: "Forex (standard lot)" },
  gold: { pipValue: 10, label: "XAUUSD (zlato)" },
  indices: { pipValue: 1, label: "Indexy (NAS100, SP500)" },
  custom: { pipValue: 10, label: "Vlastní" },
};

export default function CalculatorPage() {
  const { lang } = useT();
  const cs = lang === "cs";
  const [account, setAccount] = useState("10000");
  const [riskPct, setRiskPct] = useState("1");
  const [slPips, setSlPips] = useState("20");
  const [preset, setPreset] = useState("forex");
  const [pipValue, setPipValue] = useState("10");

  const onPreset = (p) => {
    setPreset(p);
    setPipValue(String(PRESETS[p].pipValue));
  };

  const acc = parseFloat(account) || 0;
  const rp = parseFloat(riskPct) || 0;
  const sl = parseFloat(slPips) || 0;
  const pv = parseFloat(pipValue) || 0;
  const riskUsd = acc * rp / 100;
  const lots = sl > 0 && pv > 0 ? riskUsd / (sl * pv) : 0;

  return (
    <div>
      <PageHeader
        title={cs ? "Risk kalkulačka pozic" : "Position size calculator"}
        sub={cs ? "Spočítej přesnou velikost pozice podle rizika a stop lossu" : "Compute exact position size from risk and stop loss"}
      />
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 space-y-4">
          <Input data-testid="calc-account" type="number" step="any" label={cs ? "Velikost účtu (USD)" : "Account size (USD)"} value={account} onChange={(e) => setAccount(e.target.value)} />
          <Input data-testid="calc-risk" type="number" step="any" label={cs ? "Riziko na obchod (%)" : "Risk per trade (%)"} value={riskPct} onChange={(e) => setRiskPct(e.target.value)} />
          <Input data-testid="calc-sl" type="number" step="any" label={cs ? "Stop Loss (pipy / body)" : "Stop Loss (pips / points)"} value={slPips} onChange={(e) => setSlPips(e.target.value)} />
          <Select
            data-testid="calc-preset"
            label={cs ? "Instrument" : "Instrument"}
            value={preset}
            onChange={(e) => onPreset(e.target.value)}
            options={Object.entries(PRESETS).map(([v, p]) => ({ value: v, label: p.label }))}
          />
          <Input data-testid="calc-pipvalue" type="number" step="any" label={cs ? "Hodnota pipu na 1 lot ($)" : "Pip value per 1 lot ($)"} value={pipValue} onChange={(e) => { setPipValue(e.target.value); setPreset("custom"); }} />
        </Card>
        <Card className="p-6 flex flex-col justify-center items-center text-center gap-6" data-testid="calc-results">
          <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center">
            <CalcIcon weight="bold" size={22} className="text-blue-400" />
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{cs ? "Velikost pozice" : "Position size"}</div>
            <div className="text-4xl font-mono font-medium text-blue-400 mt-1" data-testid="calc-lots">{lots > 0 ? lots.toFixed(2) : "—"} <span className="text-lg text-zinc-500">lot</span></div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{cs ? "Maximální ztráta" : "Max loss"}</div>
            <div className="text-2xl font-mono font-medium text-red-400 mt-1" data-testid="calc-maxloss">${riskUsd.toFixed(2)}</div>
          </div>
          <p className="text-xs text-zinc-600 max-w-xs">
            {cs
              ? `Při ${rp} % riziku na účtu $${acc.toLocaleString()} a SL ${sl} pipů riskuješ $${riskUsd.toFixed(0)}.`
              : `With ${rp} % risk on a $${acc.toLocaleString()} account and a ${sl}-pip SL you risk $${riskUsd.toFixed(0)}.`}
          </p>
        </Card>
      </div>
    </div>
  );
}
